import React, { type ReactNode, useCallback, useEffect, useRef } from 'react';

import { useAnalytics } from '../analytics/use-analytics';

/**
 * Tracks when a user is enrolled in an edition awareness experiment
 * Returns it as a react component
 */
export function FeatureExposedAnalytics({
	flagKey,
	cohort,
	growthInitiative,
	attributes,
	children,
}: {
	attributes?: Record<string, unknown>;
	children?: ReactNode;
	cohort: string;
	flagKey: string;
	/**
	 * This is usually the owning teams name e.g. `transmuters`
	 */
	growthInitiative?: string;
}): React.JSX.Element | null {
	const { sendAnalyticsEvent } = useAnalytics();
	const hasSent = useRef(false);

	useEffect(() => {
		if (hasSent.current) {
			return;
		}

		sendAnalyticsEvent({
			eventType: 'track',
			action: 'exposed',
			actionSubject: 'feature',
			attributes: {
				...attributes,
				flagKey,
				cohort,
				growthInitiative,
			},
		});

		hasSent.current = true;
	}, [sendAnalyticsEvent, flagKey, cohort, growthInitiative, attributes]);

	if (!children) {
		return null;
	}

	return <>{children}</>;
}

/**
 * Custom hook to track when a user is enrolled in an edition awareness experiment
 */
export type FireExperimentExposureEventType = (
	flagKey: string,
	cohort: string,
	growthInitiative?: string,
	attributes?: Record<string, unknown>,
) => void;

export const useFeatureExposedAnalytics = (): FireExperimentExposureEventType => {
	const { sendAnalyticsEvent } = useAnalytics();
	const sentFlagKeys = useRef<string[]>([]);

	const fireExperimentExposureEvent: FireExperimentExposureEventType = useCallback(
		(
			flagKey: string,
			cohort: string,
			growthInitiative?: string,
			attributes?: Record<string, unknown>,
		) => {
			if (sentFlagKeys.current.includes(flagKey)) {
				return;
			}

			sendAnalyticsEvent({
				eventType: 'track',
				action: 'exposed',
				actionSubject: 'feature',
				attributes: {
					...attributes,
					flagKey,
					cohort,
					growthInitiative,
				},
			});

			sentFlagKeys.current.push(flagKey);
		},
		[sendAnalyticsEvent],
	);

	return fireExperimentExposureEvent;
};
